import React from 'react';
import { BookOpen, ArrowUpRight } from 'lucide-react';
import SectionLabel from '../ui-shree/SectionLabel';

/**
 * IntellectualRoots — quiet "Foundations" closer to the philosophy story.
 *
 * Names the two thinkers whose work gave Shreesavaa its scientific
 * permission to operate the way it does. Tone is humble and
 * observational — these are not endorsements, they are citations. The
 * house is "standing on", not "endorsed by".
 */

const REFERENCES = [
  {
    id:       'naval',
    author:   'Naval Ravikant',
    title:    'Sell the Truth',
    house:    'On the efficiency of absolute honesty in commerce',
    body:
      'A reminder that the cheapest, most durable marketing budget is the truth itself — and that any architecture which has to lie is paying compound interest on a lie. We took this as permission to remove every unverifiable claim from our product copy and put a sensor or a hash in its place.',
    href:     'https://nav.al/',
    sourceLabel: 'nav.al',
  },
  {
    id:       'anthropic',
    author:   'The Times · Anthropic',
    title:    'The One-Person Billion-Dollar Company',
    house:    'On the leverage of Agentic Intelligence and physical truth',
    body:
      'A frame for the new shape of small companies: a single human, three or four named agents, and an unbroken physical record. Atreya, Savitri, and Indra are our reading of that frame — not a thought experiment, just a staffing plan written in Sanskrit.',
    href:     'https://www.anthropic.com/',
    sourceLabel: 'anthropic.com',
  },
];

const IntellectualRoots = ({ testId = 'home-intellectual-roots' }) => (
  <section
    id="intellectual-roots"
    data-testid={testId}
    className="px-6 md:px-12 py-24 md:py-32 bg-[#F4F8F9] border-t border-[#C1D7E2]"
  >
    <div className="max-w-[1400px] mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-14">
        <div className="md:col-span-7">
          <SectionLabel testId="intellectual-roots-label">
            Foundations · Intellectual Roots
          </SectionLabel>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#7C9CB1] font-light leading-[1.05]">
            We did not invent this.
            <br />
            <em className="text-[#90B0BF]">We read.</em>
          </h2>
        </div>
        <div className="md:col-span-5 md:pt-4">
          <p className="font-serif italic text-lg text-[#7C9CB1]/90 leading-relaxed max-w-md">
            The Shreesavaa architecture is built on two scientific
            permissions — public ideas from people who thought longer than
            we did. We list them not as endorsements, but as honest
            attribution.
          </p>
        </div>
      </div>

      <ol className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
        {REFERENCES.map((r, i) => (
          <li
            key={r.id}
            data-testid={`intellectual-root-${r.id}`}
            className="relative border border-[#C1D7E2] bg-[#F4F8F9] p-8 md:p-10 flex flex-col"
          >
            <span
              aria-hidden
              className="absolute top-0 left-8 right-8 h-px"
              style={{
                background:
                  'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)',
              }}
            />
            <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#90B0BF] mb-5">
              Reading {String(i + 1).padStart(2, '0')}
            </p>
            <BookOpen size={24} strokeWidth={1.2} className="text-[#7C9CB1] mb-5" />
            <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#7C9CB1] mb-2">
              {r.author}
            </p>
            <h3 className="font-serif text-2xl md:text-3xl text-[#7C9CB1] font-light leading-tight">
              {r.title}
            </h3>
            <p
              data-testid={`intellectual-root-${r.id}-house`}
              className="mt-3 font-serif italic text-[#90B0BF] text-base leading-relaxed"
            >
              {r.house}
            </p>
            <p className="mt-5 text-[#7C9CB1]/85 font-light leading-relaxed">
              {r.body}
            </p>
            <a
              href={r.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`intellectual-root-${r.id}-link`}
              className="mt-6 group inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-[#7C9CB1] hover:text-[#90B0BF] transition-colors"
            >
              <span className="border-b border-[#7C9CB1]/40 group-hover:border-[#90B0BF] pb-0.5 transition-colors">
                {r.sourceLabel}
              </span>
              <ArrowUpRight
                size={11}
                strokeWidth={1.5}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          </li>
        ))}
      </ol>

      <p
        data-testid="intellectual-roots-footer"
        className="mt-12 font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] text-center max-w-2xl mx-auto leading-relaxed"
      >
        Cited, not co-signed · Read at your own pace
      </p>
    </div>
  </section>
);

export default IntellectualRoots;
